// The vocabulary of an image edit, shared by the editor UI, the live preview
// and the `editImage` mutation.
//
// Every colour field below is named after — and carries the same neutral value
// as — the CSS `filter` function that implements it. That is what makes the
// preview trustworthy: `cssFilter()` turns these numbers into a real filter
// chain the browser applies to an <img>, and the server feeds the identical
// numbers into the equivalent ffmpeg filters, in the same order (see
// services/src/common/aws/s3/image-edit.ts). Adding a knob here means adding
// it in both those places too, or the preview starts lying.
//
// mobileapp/src/utils/imageEdit.ts is the deliberate mirror of this file — the
// two apps share no build, so the spec is kept in sync by hand.

export interface ImageCrop {
  /** All four are fractions (0..1) of the *flipped and rotated* image. */
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ImageEdit {
  // Geometry. Applied flip -> rotate -> crop, which is the order the preview's
  // own CSS transform composes in.
  flipHorizontal: boolean;
  flipVertical: boolean;
  /** Clockwise degrees; only 0/90/180/270. */
  rotate: number;
  crop: ImageCrop;

  // Colour, applied in exactly this order.
  brightness: number;
  contrast: number;
  saturate: number;
  hueRotate: number;
  sepia: number;
  grayscale: number;
  invert: number;
  /** Gaussian radius in *source* pixels — see previewBlurPx for why. */
  blur: number;
}

export const FULL_CROP: ImageCrop = { x: 0, y: 0, width: 1, height: 1 };

export const NEUTRAL_EDIT: ImageEdit = {
  flipHorizontal: false,
  flipVertical: false,
  rotate: 0,
  crop: FULL_CROP,
  brightness: 1,
  contrast: 1,
  saturate: 1,
  hueRotate: 0,
  sepia: 0,
  grayscale: 0,
  invert: 0,
  blur: 0,
};

// Mirrors the server's own floor (MIN_CROP_SIZE in image-edit.ts) so the UI
// can't hand it a box it would silently widen.
export const MIN_CROP_SIZE = 0.02;

// --- Adjustment sliders ----------------------------------------------------

export type AdjustmentKey =
  | "brightness"
  | "contrast"
  | "saturate"
  | "hueRotate"
  | "sepia"
  | "grayscale"
  | "invert"
  | "blur";

export interface AdjustmentSpec {
  key: AdjustmentKey;
  label: string;
  min: number;
  max: number;
  step: number;
  /** The value at which this adjustment does nothing. */
  neutral: number;
  /** Renders the raw number as something a person can read. */
  format: (value: number) => string;
}

const asPercent = (value: number) => `${Math.round(value * 100)}%`;
const asOffsetPercent = (value: number) => `${value > 1 ? "+" : ""}${Math.round((value - 1) * 100)}%`;

export const ADJUSTMENTS: AdjustmentSpec[] = [
  { key: "brightness", label: "Brightness", min: 0.2, max: 2, step: 0.01, neutral: 1, format: asOffsetPercent },
  { key: "contrast", label: "Contrast", min: 0.2, max: 2, step: 0.01, neutral: 1, format: asOffsetPercent },
  { key: "saturate", label: "Saturation", min: 0, max: 2.5, step: 0.01, neutral: 1, format: asOffsetPercent },
  { key: "hueRotate", label: "Hue", min: -180, max: 180, step: 1, neutral: 0, format: (v) => `${Math.round(v)}°` },
  { key: "sepia", label: "Warmth", min: 0, max: 1, step: 0.01, neutral: 0, format: asPercent },
  { key: "grayscale", label: "Desaturate", min: 0, max: 1, step: 0.01, neutral: 0, format: asPercent },
  { key: "invert", label: "Invert", min: 0, max: 1, step: 0.01, neutral: 0, format: asPercent },
  { key: "blur", label: "Blur", min: 0, max: 40, step: 0.5, neutral: 0, format: (v) => `${v.toFixed(1)} px` },
];

// --- Presets ---------------------------------------------------------------

export interface FilterPreset {
  id: string;
  label: string;
  /** Only the colour fields — a preset never moves the crop or rotation. */
  values: Partial<Omit<ImageEdit, "crop" | "rotate" | "flipHorizontal" | "flipVertical">>;
}

// Each preset is nothing but a set of the adjustments above, which is exactly
// why they survive the round-trip: the server has no notion of "Vivid", it
// just receives the numbers the preset filled in.
export const FILTER_PRESETS: FilterPreset[] = [
  { id: "original", label: "Original", values: {} },
  { id: "vivid", label: "Vivid", values: { saturate: 1.45, contrast: 1.15, brightness: 1.04 } },
  { id: "punch", label: "Punch", values: { contrast: 1.35, saturate: 1.2, brightness: 0.98 } },
  { id: "warm", label: "Warm", values: { sepia: 0.35, saturate: 1.15, brightness: 1.06 } },
  { id: "golden", label: "Golden", values: { sepia: 0.55, saturate: 1.25, contrast: 1.08, brightness: 1.08 } },
  { id: "cool", label: "Cool", values: { hueRotate: -12, saturate: 1.1, brightness: 1.04 } },
  { id: "arctic", label: "Arctic", values: { hueRotate: -22, saturate: 0.85, contrast: 1.12, brightness: 1.08 } },
  { id: "mono", label: "Mono", values: { grayscale: 1, contrast: 1.12 } },
  { id: "noir", label: "Noir", values: { grayscale: 1, contrast: 1.45, brightness: 0.92 } },
  { id: "silver", label: "Silver", values: { grayscale: 1, contrast: 0.9, brightness: 1.12 } },
  { id: "vintage", label: "Vintage", values: { sepia: 0.6, saturate: 0.8, contrast: 0.92, brightness: 1.05 } },
  { id: "faded", label: "Faded", values: { contrast: 0.78, saturate: 0.7, brightness: 1.12 } },
  { id: "matte", label: "Matte", values: { contrast: 0.85, saturate: 0.9, sepia: 0.12, brightness: 1.04 } },
  { id: "dramatic", label: "Dramatic", values: { contrast: 1.5, saturate: 0.75, brightness: 0.94 } },
  { id: "sunset", label: "Sunset", values: { sepia: 0.3, hueRotate: -10, saturate: 1.4, contrast: 1.1 } },
  { id: "studio", label: "Studio", values: { brightness: 1.12, contrast: 1.08, saturate: 1.05 } },
];

/** Applies a preset on top of the current geometry, replacing all colour values. */
export const applyPreset = (edit: ImageEdit, preset: FilterPreset): ImageEdit => ({
  ...edit,
  brightness: NEUTRAL_EDIT.brightness,
  contrast: NEUTRAL_EDIT.contrast,
  saturate: NEUTRAL_EDIT.saturate,
  hueRotate: NEUTRAL_EDIT.hueRotate,
  sepia: NEUTRAL_EDIT.sepia,
  grayscale: NEUTRAL_EDIT.grayscale,
  invert: NEUTRAL_EDIT.invert,
  blur: NEUTRAL_EDIT.blur,
  ...preset.values,
});

// --- Preview helpers -------------------------------------------------------

const isNeutral = (value: number, neutral: number) => Math.abs(value - neutral) < 0.0005;

/**
 * The CSS `filter` value for this edit's colour half.
 *
 * `scale` converts the stored blur (which is in source pixels, so the server
 * can act on it without knowing how big anyone's preview happened to be) into
 * the displayed pixels CSS wants. Pass displayedWidth / naturalWidth.
 */
export const cssFilter = (edit: ImageEdit, scale = 1): string => {
  const parts: string[] = [];
  if (!isNeutral(edit.brightness, 1)) parts.push(`brightness(${edit.brightness})`);
  if (!isNeutral(edit.contrast, 1)) parts.push(`contrast(${edit.contrast})`);
  if (!isNeutral(edit.saturate, 1)) parts.push(`saturate(${edit.saturate})`);
  if (!isNeutral(edit.hueRotate, 0)) parts.push(`hue-rotate(${edit.hueRotate}deg)`);
  if (!isNeutral(edit.sepia, 0)) parts.push(`sepia(${edit.sepia})`);
  if (!isNeutral(edit.grayscale, 0)) parts.push(`grayscale(${edit.grayscale})`);
  if (!isNeutral(edit.invert, 0)) parts.push(`invert(${edit.invert})`);
  if (edit.blur > 0.01) parts.push(`blur(${(edit.blur * scale).toFixed(2)}px)`);
  return parts.length ? parts.join(" ") : "none";
};

/**
 * The CSS `transform` for this edit's geometry half. Written rotate-then-scale
 * because CSS applies transforms right to left, which makes this "flip, then
 * rotate" — the order the server applies them in.
 */
export const cssTransform = (edit: ImageEdit): string => {
  const parts: string[] = [];
  if (edit.rotate) parts.push(`rotate(${edit.rotate}deg)`);
  if (edit.flipHorizontal) parts.push("scaleX(-1)");
  if (edit.flipVertical) parts.push("scaleY(-1)");
  return parts.length ? parts.join(" ") : "none";
};

/** True when rotation has put the image on its side, swapping its aspect ratio. */
export const isQuarterTurned = (edit: ImageEdit): boolean =>
  edit.rotate === 90 || edit.rotate === 270;

export const hasCrop = (edit: ImageEdit): boolean =>
  edit.crop.x > 0.001 ||
  edit.crop.y > 0.001 ||
  edit.crop.width < 0.999 ||
  edit.crop.height < 0.999;

export const hasColourChange = (edit: ImageEdit): boolean => cssFilter(edit) !== "none";

/** Whether applying this edit would produce anything different at all. */
export const hasAnyChange = (edit: ImageEdit): boolean =>
  hasCrop(edit) || hasColourChange(edit) || !!edit.rotate || edit.flipHorizontal || edit.flipVertical;

/** The shape the `editImage` mutation takes — drops nothing, but orders it explicitly. */
export const toEditInput = (edit: ImageEdit) => ({
  flipHorizontal: edit.flipHorizontal,
  flipVertical: edit.flipVertical,
  rotate: edit.rotate,
  crop: hasCrop(edit)
    ? { x: edit.crop.x, y: edit.crop.y, width: edit.crop.width, height: edit.crop.height }
    : null,
  brightness: edit.brightness,
  contrast: edit.contrast,
  saturate: edit.saturate,
  hueRotate: edit.hueRotate,
  sepia: edit.sepia,
  grayscale: edit.grayscale,
  invert: edit.invert,
  blur: edit.blur,
});

// --- Crop aspect ratios ----------------------------------------------------

export interface AspectRatio {
  id: string;
  label: string;
  /** width / height, or null for a freely draggable box. */
  value: number | null;
}

export const ASPECT_RATIOS: AspectRatio[] = [
  { id: "free", label: "Free", value: null },
  { id: "1:1", label: "1:1", value: 1 },
  { id: "4:3", label: "4:3", value: 4 / 3 },
  { id: "3:4", label: "3:4", value: 3 / 4 },
  { id: "16:9", label: "16:9", value: 16 / 9 },
  { id: "9:16", label: "9:16", value: 9 / 16 },
  { id: "3:2", label: "3:2", value: 3 / 2 },
  { id: "2:3", label: "2:3", value: 2 / 3 },
];
